import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthFetch } from '../hooks/useAuthFetch';

export default function GlobalSearch({ collapsed }) {
  const fetchWithAuth = useAuthFetch();
  const navigate = useNavigate();
  const [query, setQuery]       = useState('');
  const [results, setResults]   = useState([]);
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [active, setActive]     = useState(-1);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setOpen(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res  = await fetchWithAuth(`/api/search?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        setResults(data.results || []);
        setOpen(true);
        setActive(-1);
      } catch { /* silencieux */ }
      finally { setLoading(false); }
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const STATE_KEY = { client: 'clientId', contact: 'contactId', contrat: 'contratId', opportunite: 'oppId', devis: 'devisId', facture: 'factureId' };

  function go(result) {
    const stateKey = STATE_KEY[result.type];
    navigate(result.path, stateKey ? { state: { [stateKey]: result.id } } : {});
    setQuery('');
    setOpen(false);
    setActive(-1);
  }

  function handleKey(e) {
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(a => Math.max(a - 1, -1)); }
    if (e.key === 'Enter' && active >= 0) { go(results[active]); }
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  }

  // En mode réduit : icône loupe qui navigue vers Clients (pas de champ dans la sidebar réduite)
  if (collapsed) {
    return (
      <button
        onClick={() => navigate('/clients')}
        style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 18, padding: '8px 0', width: '100%', display: 'flex', justifyContent: 'center' }}
        title="Rechercher"
      >
        🔍
      </button>
    );
  }

  return (
    <div style={{ position: 'relative', marginBottom: 8 }}>
      <div style={{ position: 'relative' }}>
        <span style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 14, opacity: 0.4, pointerEvents: 'none' }}>🔍</span>
        <input
          ref={inputRef}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKey}
          onFocus={() => query.length >= 2 && results.length > 0 && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Rechercher..."
          style={{
            width: '100%', boxSizing: 'border-box',
            background: '#0f1117', border: '1px solid #2a2d3e', borderRadius: 8,
            color: '#e2e8f0', fontSize: 12, padding: '7px 10px 7px 30px',
            fontFamily: 'inherit', outline: 'none',
          }}
        />
        {loading && (
          <span style={{ position: 'absolute', right: 9, top: '50%', transform: 'translateY(-50%)', fontSize: 11, color: '#64748b' }}>…</span>
        )}
      </div>

      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 8,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 1000, overflow: 'hidden',
          maxHeight: 360, overflowY: 'auto',
        }}>
          {results.map((r, i) => (
            <button
              key={`${r.type}-${r.id}`}
              onMouseDown={() => go(r)}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', background: i === active ? '#41B07320' : 'transparent',
                border: 'none', borderBottom: '1px solid #2a2d3e', cursor: 'pointer',
                textAlign: 'left',
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span style={{ fontSize: 15, flexShrink: 0 }}>{r.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#e2e8f0', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.label}
                </div>
                {r.sublabel && (
                  <div style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {r.sublabel}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 10, color: '#4a5568', background: '#0f1117', borderRadius: 4, padding: '1px 6px', flexShrink: 0, textTransform: 'capitalize' }}>
                {r.type}
              </span>
            </button>
          ))}
        </div>
      )}

      {open && query.length >= 2 && results.length === 0 && !loading && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
          background: '#1a1d2e', border: '1px solid #2a2d3e', borderRadius: 8,
          padding: '12px 14px', fontSize: 12, color: '#64748b', zIndex: 1000,
        }}>
          Aucun résultat pour « {query} »
        </div>
      )}
    </div>
  );
}
